import { InputBase } from "@mui/material";
import { GridCustomCellProps } from "@progress/kendo-react-grid";
import React, { useEffect, useMemo, useState } from "react";
import { TextCustomCell } from "./text-custom-cell.component";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import "./custom-cells.css";
import {
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { useCatalogsStore } from "@/dentlab/src/store/Catalogs";
import { useTableCellNavigation } from "./use-table-cell-navigation.hook";
import { useBulkChange } from "./use-bulk-change.hook";

export const PriceSelect: React.FC<GridCustomCellProps> = (props) => {
	const [price, setPrice] = useState<number | string>("");
	const [openDropdown, setOpenDropdown] = useState(false);
	const { tariffsLookup } = useCatalogsStore((state) => ({
		tariffsLookup: state.tariffsLookup
	}));

	const { handleChange: handleBulkChange } = useBulkChange({
		dataItem: props.dataItem,
		dataIndex: props.dataIndex,
		field: props.field,
		onChange: props.onChange,
	});

	const { handleKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
		field: props.field,
		dataIndex: props.dataIndex,
		focusSelector: (targetField: string, targetRowIndex: number) =>
			`input[data-field="${targetField}"][data-row-index="${targetRowIndex}"]`,
	});

	const isTariff = props.dataItem?.type === JobItemTypeEnum.TARIFF;
	const potentialTariffs = isTariff ? tariffsLookup[props.dataItem?.code_e] : null;

	useEffect(() => {
		if (props.field) {
			setPrice(props.dataItem[props.field] ?? "");
		}
	}, [props.dataItem, props.field]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key.toLowerCase() === "enter") {
			handleChange(event);
			(event.target as HTMLInputElement).blur();
			return;
		}

		if (event.key === "Backspace" && event.target instanceof HTMLInputElement && event.target.value === "") {
			event.preventDefault();
			handleBulkChange(0, event.metaKey || event.ctrlKey, event.shiftKey, event.altKey);
			return;
		}

		if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
			const input = event.target as HTMLInputElement;
			if (event.key === "ArrowRight") {
				const isAtEnd = input.selectionStart === input.value.length;
				if (!isAtEnd) return;
			} else {
				const isAtStart = input.selectionStart === 0;
				if (!isAtStart) return;
			}
		}

		handleNavigationKeyDown(event);
	};

	const tariff = potentialTariffs ? Object.values(potentialTariffs)[0] : null;

	const options: { index: number; price: string; label: string }[] = useMemo(() =>
		tariff?.price_items?.map((item, index) => ({
			index,
			price: item.price?.toString() ?? "",
			label: item.price_list_name ?? "",
		})) ?? []
	, [tariff?.price_items]);

	const noTariffsFound = !potentialTariffs || Object.keys(potentialTariffs).length === 0
	const moreThanOneTariffFound = Object.keys(potentialTariffs ?? {}).length > 1
	const tariffHasNoPriceItems = !tariff || !tariff.price_items || tariff.price_items.length === 0

	if (noTariffsFound || moreThanOneTariffFound || tariffHasNoPriceItems) {
		return <TextCustomCell {...props} />;
	}

	const handleChange = (e: React.SyntheticEvent<EventTarget, Event>) => {
		// @ts-expect-error value is actually present
		const value = e.target.value === "" ? "" : Number(e.target.value);

		if (value === "") {
			setPrice(0);
			return;
		}

		if (isNaN(value)) {
			showNotification({
				message: "Bitte geben Sie eine gültige Zahl ein.",
				type: "error",
			});
			// @ts-expect-error value is actually present
			setPrice(props.dataItem[props.field]);
			return;
		}

		const maxPrice = Math.max(...options.map((o) => Number(o.price)));
		const minPrice = Math.min(...options.map((o) => Number(o.price)));
		if (value > maxPrice || value < minPrice) {
			showNotification({
				message:
					"Gewählter Preis ist ausserhalb der Tarifpunktgrenzen!",
				type: "warning",
			});
		}

		if (props.onChange) {
			props.onChange({
				dataItem: props.dataItem,
				field: props.field,
				syntheticEvent: e,
				value,
				dataIndex: props.dataIndex,
			});
		}

		setPrice(value);
	};

	return (
		<td
			style={{
				fontSize: "12px",
				display: "flex",
				padding: "2px",
			}}
		>
			<InputBase
				value={price}
				onKeyDown={handleKeyDown}
				onBlur={handleChange}
				onChange={(e) => setPrice(e.target.value)}
				fullWidth
				inputProps={{
					"data-field": props.field,
					"data-row-index": props.dataIndex,
				}}
				sx={{
					height: "22px",
					fontSize: "12px",
					paddingLeft: 1,
					textAlign: "right",
					"& input": {
						textOverflow: "ellipsis",
						padding: 0,
					},
					"&:hover": {
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
					"&.Mui-focused": {
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
				}}
			/>
			<DropdownMenu open={openDropdown} onOpenChange={setOpenDropdown}>
				<DropdownMenuTrigger asChild>
					<div
						className={clsx(
							"rounded-sm h-[22px] rounded-l-none flex items-center ",
							{
								"border-neutral-400/70 border-l-1 border":
									!openDropdown,
								"border-[#036] border-2 border-l-2":
									openDropdown,
							}
						)}
					>
						<ChevronDown size={16} />
					</div>
				</DropdownMenuTrigger>
				<DropdownMenuContent>
					{options.map((o) => {
						return (
							<DropdownMenuItem
								key={o.index}
								onClick={async () => {
									setPrice(Number(o.price));
									handleChange({
										target: { value: o.price },
									} as any);
								}}
								className="flex justify-between items-center cursor-pointer"
							>
								<span>{o.price}</span>
								<span>({o.label})</span>
							</DropdownMenuItem>
						);
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		</td>
	);
};